/* Progressive only: retain select/form contracts and let the existing handlers sort. */
(() => {
  if (window.terplaxCollectionSortLoaded) return;
  window.terplaxCollectionSortLoaded = true;
  const icon = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4 6h10M4 12h7M4 18h4M18 5v14m-3-3 3 3 3-3"/></svg>';
  class CollectionSort extends HTMLElement {
    connectedCallback() {
      if (this.controller || !this.querySelector('select')) return;
      const select = this.querySelector('select');
      const menu = this.querySelector('[popover]');
      const trigger = this.querySelector('button');
      if (!menu?.showPopover || !trigger) return;
      this.select = select; this.menu = menu; this.trigger = trigger;
      this.controller = new AbortController();
      const {signal} = this.controller;
      let consumedEscape = false;
      const nativeModalActive = () => [...document.querySelectorAll(
        'dialog:modal, [aria-modal="true"], details-modal details[open] [role="dialog"], .header details[open] .search-modal'
      )].some(modal => {
        // The sort can live inside its own filter drawer; only competing modals take priority.
        if (modal.contains(this) || modal.closest('[hidden], [inert], [aria-hidden="true"], details:not([open])')) return false;
        if (modal.checkVisibility) return modal.checkVisibility({checkOpacity: true, checkVisibilityCSS: true});
        const style = getComputedStyle(modal);
        return modal.getClientRects().length > 0 && style.display !== 'none' && style.visibility === 'visible' && Number(style.opacity) > 0;
      });
      const close = (restore = false) => {
        if (menu.matches(':popover-open')) menu.hidePopover();
        trigger.setAttribute('aria-expanded', 'false');
        if (restore && trigger.isConnected) trigger.focus();
      };
      const position = () => {
        if (!menu.matches(':popover-open')) return;
        const b = trigger.getBoundingClientRect();
        const height = menu.getBoundingClientRect().height;
        const width = menu.getBoundingClientRect().width;
        menu.style.left = `${Math.max(12, Math.min(b.right - width, innerWidth - width - 12))}px`;
        menu.style.top = `${Math.max(12, b.bottom + 6 + height <= innerHeight - 12 ? b.bottom + 6 : b.top - height - 6)}px`;
      };
      const options = () => [...menu.querySelectorAll('button:not(:disabled)')];
      const sync = () => {
        trigger.disabled = select.disabled;
        const label = this.dataset.sortLabel || select.labels?.[0]?.textContent.trim() || '';
        trigger.setAttribute('aria-label', `${label} ${select.selectedOptions[0]?.textContent.trim() || ''}`.trim());
        trigger.title = trigger.getAttribute('aria-label');
        menu.querySelectorAll('button').forEach(item => item.setAttribute('aria-checked', String(item.dataset.value === select.value)));
      };
      const open = (last = false) => {
        if (trigger.disabled) return;
        document.querySelectorAll('terplax-collection-sort').forEach(other => {
          if (other !== this) other.closeSort?.();
        });
        menu.showPopover(); trigger.setAttribute('aria-expanded', 'true'); position();
        const items = options();
        (last ? items.at(-1) : items.find(item => item.getAttribute('aria-checked') === 'true') || items[0])?.focus();
      };
      this.closeSort = close;
      menu.replaceChildren();
      [...select.options].forEach(option => {
        const item = document.createElement('button');
        item.type = 'button'; item.className = 'tplx-collection-sort__option';
        item.setAttribute('role', 'menuitemradio'); item.tabIndex = -1;
        item.textContent = option.textContent; item.dataset.value = option.value; item.disabled = option.disabled;
        item.addEventListener('click', () => {
          const changed = select.value !== option.value;
          select.value = option.value;
          sync(); close(true);
          if (changed) {
            // Dawn uses input; Flavor and Shop Browser use change. Existing handlers own submission.
            select.dispatchEvent(new Event('input', {bubbles: true}));
            select.dispatchEvent(new Event('change', {bubbles: true}));
          }
        }, {signal});
        menu.append(item);
      });
      trigger.addEventListener('click', () => menu.matches(':popover-open') ? close() : open(), {signal});
      trigger.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault(); open(event.key === 'ArrowUp');
        }
      }, {signal});
      menu.addEventListener('keydown', event => {
        const items = options(); const index = items.indexOf(document.activeElement);
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault();
          const target = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
          items[target]?.focus();
        }
        if (event.key === 'Tab') close(true);
      }, {signal});
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && nativeModalActive()) {
          consumedEscape = false; close(false); return;
        }
        if (event.key === 'Escape' && menu.matches(':popover-open')) {
          consumedEscape = true;
          event.preventDefault(); event.stopPropagation(); close(true);
        }
      }, {signal, capture: true});
      document.addEventListener('keyup', event => {
        if (event.key === 'Escape' && nativeModalActive()) {
          consumedEscape = false; close(false); return;
        }
        if (event.key === 'Escape' && consumedEscape) {
          consumedEscape = false;
          event.preventDefault(); event.stopPropagation(); close(true);
        }
      }, {signal, capture: true});
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) close(); }, {signal});
      this.addEventListener('focusout', event => {
        // Pointer blur briefly reports BODY before focus reaches the next menu item.
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        cancelAnimationFrame(this.focusOutFrame);
        this.focusOutFrame = requestAnimationFrame(() => {
          this.focusOutFrame = null;
          if (this.isConnected && !this.contains(document.activeElement)) close();
        });
      }, {signal});
      select.addEventListener('change', sync, {signal});
      menu.addEventListener('toggle', () => trigger.setAttribute('aria-expanded', String(menu.matches(':popover-open'))), {signal});
      window.addEventListener('resize', position, {signal});
      window.addEventListener('scroll', position, {signal, capture: true, passive: true});
      select.hidden = true; trigger.hidden = false; this.dataset.enhanced = 'true'; sync();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.focusOutFrame); this.focusOutFrame = null;
      this.closeSort?.(); this.controller?.abort(); this.controller = null;
    }
  }
  if (!customElements.get('terplax-collection-sort')) customElements.define('terplax-collection-sort', CollectionSort);
  const adaptExistingSelects = () => {
    document.querySelectorAll('.tplx-collection-native select[name="sort_by"], .tp-shop-browser__sort select[name="sort_by"]').forEach(select => {
      if (select.closest('terplax-collection-sort')) return;
      const label = select.labels?.[0]?.textContent.trim() || '';
      const parent = select.parentElement;
      const root = document.createElement('terplax-collection-sort'); root.className = 'tplx-collection-sort'; root.dataset.sortLabel = label;
      const menuId = `${select.id}-IconMenu`;
      const trigger = document.createElement('button'); trigger.type = 'button'; trigger.className = 'tplx-collection-sort__trigger'; trigger.hidden = true;
      trigger.setAttribute('aria-haspopup', 'menu'); trigger.setAttribute('aria-expanded', 'false'); trigger.setAttribute('aria-controls', menuId); trigger.setAttribute('aria-label', label); trigger.innerHTML = icon;
      const menu = document.createElement('div'); menu.id = menuId; menu.className = 'tplx-collection-sort__menu'; menu.setAttribute('role', 'menu'); menu.setAttribute('aria-label', label); menu.setAttribute('popover', 'manual');
      root.append(select, trigger, menu); parent.append(root);
      if (root.dataset.enhanced) parent.classList.add('tplx-collection-sort-adapted');
    });
  };
  let queued = false;
  const queueAdapt = () => {
    if (queued) return;
    queued = true; queueMicrotask(() => { queued = false; adaptExistingSelects(); });
  };
  adaptExistingSelects();
  new MutationObserver(records => {
    if (records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('select,facet-filters-form,.tplx-collection-native') || node.querySelector('select[name="sort_by"]'))))) queueAdapt();
  }).observe(document.body, {childList: true, subtree: true});
})();
